/**
 * Product Card Initialization & Interaction
 * 
 * 1. IMAGE INITIALIZATION:
 *    Ensures the correct variant image is shown on initial load based on the URL variant parameter
 *    or the pre-selected variant from search results.
 * 
 * 2. SWATCH INTERACTION:
 *    Updates the product card URL immediately when a user selects a color swatch.
 *    (The theme's default behavior sometimes lags or doesn't update the URL on cards correctly)
 */

import { onDocumentReady } from '@theme/utilities';

/**
 * Initializes product cards to show the correct variant image
 * The URL is handled by Liquid server-side rendering, so we only need to sync the image.
 */
function initializeProductCardImages() {
  const productCards = document.querySelectorAll('product-card');

  productCards.forEach((card) => {
    // Skip if already initialized
    if (card.hasAttribute('data-image-initialized')) return;

    const productId = card.dataset.productId;
    console.log(`🔍 INIT: Processing product card ${productId}`);

    // Get the variant picker if it exists
    const variantPicker = card.querySelector('swatches-variant-picker-component');
    if (!variantPicker) {
      console.log(`  ⚠️  No variant picker found`);
      return;
    }

    // Get the selected option
    // @ts-ignore - selectedOption is a custom property on the variant picker component
    const selectedOption = variantPicker.selectedOption;
    if (!selectedOption) {
      console.log(`  ⚠️  No selected option found`);
      return;
    }

    // Get the media ID for the selected variant
    const selectedMediaId = selectedOption.dataset.optionMediaId;
    console.log(`  Selected media ID: ${selectedMediaId}`);

    // Only update images if we have a mediaId
    if (!selectedMediaId) {
      console.log(`  ⚠️  No media ID for selected variant`);
      card.setAttribute('data-image-initialized', 'true');
      return;
    }

    // Get the slideshow component
    const slideshow = card.querySelector('slideshow-component');
    if (!slideshow) {
      console.log(`  ⚠️  No slideshow found`);
      card.setAttribute('data-image-initialized', 'true');
      return;
    }

    // Find all slides
    const slides = slideshow.querySelectorAll('slideshow-slide');
    console.log(`  Total slides found: ${slides.length}`);

    let hiddenCount = 0;
    let visibleCount = 0;

    // Hide ALL variant images except the selected one
    slides.forEach((slide, index) => {
      const isVariantImage = slide.hasAttribute('variant-image');
      const slideId = slide.getAttribute('slide-id');

      console.log(`  📸 Slide ${index + 1}: ID=${slideId}, isVariant=${isVariantImage}, currentlyHidden=${slide.hidden}`);

      if (isVariantImage && slideId !== selectedMediaId) {
        // @ts-ignore - hidden is a standard HTMLElement property
        slide.hidden = true;
        hiddenCount++;
        console.log(`    ❌ HIDING (variant image, not selected)`);
      } else if (isVariantImage && slideId === selectedMediaId) {
        // @ts-ignore
        slide.hidden = false;
        visibleCount++;
        console.log(`    ✅ SHOWING (selected variant image)`);
      } else if (!isVariantImage) {
        visibleCount++;
        console.log(`    ✅ Generic image (keeping as is)`);
      }
    });

    console.log(`  📊 Init Summary: ${visibleCount} visible, ${hiddenCount} hidden`);

    // Select the correct slide without animation
    // @ts-ignore - select is a custom method on the slideshow component
    if (typeof slideshow.select === 'function') {
      console.log(`  🎯 Selecting slide with ID: ${selectedMediaId}`);
      // @ts-ignore
      slideshow.select({ id: selectedMediaId }, undefined, { animate: false });
    }

    // Mark as initialized
    card.setAttribute('data-image-initialized', 'true');
  });
}

/**
 * Sets up a global event listener for variant changes
 * Uses event delegation to catch all swatch change events immediately
 */
function setupGlobalVariantListener() {
  // Only set up once
  // @ts-ignore - custom property on window
  if (window.__variantListenerSetup) return;
  // @ts-ignore - custom property on window
  window.__variantListenerSetup = true;
  
  // Listen for change events on swatch inputs (event delegation)
  // We use 'change' instead of 'variant:update' because the latter 
  // might not fire on product cards or might fire too late.
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    
    // Check if this is a swatch input in a product card
    if (!target.name?.includes('-swatch')) return;
    
    const productCard = target.closest('product-card');
    if (!productCard) return;
    
    // Get the variant ID from the selected swatch
    const variantId = target.dataset.variantId;
    if (!variantId) return;
    
    // Update the product card link URL immediately
    const productCardLink = productCard.querySelector('a[ref="productCardLink"]');
    if (productCardLink instanceof HTMLAnchorElement) {
      const currentUrl = new URL(productCardLink.href);
      currentUrl.searchParams.set('variant', variantId);
      productCardLink.href = currentUrl.toString();
    }
  }, true); // Use capture phase to ensure we run before other handlers
}

// Run on document ready
onDocumentReady(() => {
  // 1. Set up interaction listeners
  setupGlobalVariantListener();
  
  // 2. Initialize images (one-time pass)
  // We use a small delay to ensure custom elements are upgraded
  setTimeout(initializeProductCardImages, 100);
  
  // 3. Watch for dynamically loaded products (pagination, infinite scroll)
  const observer = new MutationObserver((mutations) => {
    let shouldReinitialize = false;
    
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element && (node.tagName === 'PRODUCT-CARD' || node.querySelector('product-card'))) {
          shouldReinitialize = true;
        }
      });
    });
    
    if (shouldReinitialize) {
      // Initialize images for new cards
      setTimeout(initializeProductCardImages, 50);
    }
  });
  
  // Observe the document body for new product cards
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});
